/* Lithos product page — pincode delivery estimate */
(() => {
  const STORE_KEY = 'lithos_pincode';
  const DAY = 24 * 60 * 60 * 1000;

  const storage = {
    get() {
      try {
        return window.localStorage.getItem(STORE_KEY) || '';
      } catch {
        return '';
      }
    },
    set(value) {
      try {
        window.localStorage.setItem(STORE_KEY, value);
      } catch {
        /* storage unavailable */
      }
    },
  };

  /** Today's date in India (IST), as a plain calendar date. */
  const todayIST = () => {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
    const [y, m, d] = parts.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d));
  };

  /** Adds working days, skipping Sundays. */
  const addWorkingDays = (start, days) => {
    const date = new Date(start.getTime());
    let added = 0;
    while (added < days) {
      date.setTime(date.getTime() + DAY);
      if (date.getUTCDay() !== 0) added += 1;
    }
    return date;
  };

  const fmt = (date, withWeekday = true) =>
    new Intl.DateTimeFormat('en-IN', {
      timeZone: 'UTC',
      weekday: withWeekday ? 'short' : undefined,
      day: 'numeric',
      month: 'short',
    }).format(date);

  const cache = new Map();

  /** Looks up the district / state for an Indian pincode. Fails quietly. */
  async function lookup(pin) {
    if (cache.has(pin)) return cache.get(pin);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 3500);
    let result = null;
    try {
      const res = await fetch(`https://api.postalpincode.in/pincode/${pin}`, { signal: controller.signal });
      if (res.ok) {
        const data = await res.json();
        const office = Array.isArray(data) && data[0]?.Status === 'Success' ? data[0].PostOffice?.[0] : null;
        if (office) result = { district: office.District || '', state: office.State || '' };
        else if (Array.isArray(data) && data[0]?.Status === 'Error') result = { invalid: true };
      }
    } catch {
      result = null;
    } finally {
      clearTimeout(timer);
    }
    cache.set(pin, result);
    return result;
  }

  function init(root) {
    if (!(root instanceof HTMLElement) || root.dataset.lptReady) return;
    root.dataset.lptReady = '1';

    const form = root.querySelector('[data-lpt-pin-form]');
    const input = root.querySelector('[data-lpt-pin-input]');
    const btn = root.querySelector('[data-lpt-pin-btn]');
    const eta = root.querySelector('[data-lpt-eta]');
    const main = root.querySelector('[data-lpt-eta-main]');
    const sub = root.querySelector('[data-lpt-eta-sub]');
    const error = root.querySelector('[data-lpt-pin-error]');
    const hint = root.querySelector('[data-lpt-hint]');
    if (!form || !(input instanceof HTMLInputElement) || !btn || !eta || !main || !sub) return;

    const ahdText = root.dataset.ahdText || 'Delivery within 2 hours';
    const gujDays = Math.max(0, Number(root.dataset.gujDays || 1));
    const minDays = Math.max(1, Number(root.dataset.minDays || 3));
    const maxDays = Math.max(minDays, Number(root.dataset.maxDays || 7));

    const setMode = (mode) => {
      btn.dataset.mode = mode;
      btn.textContent = mode === 'change' ? 'Change' : 'Check';
      input.readOnly = mode === 'change';
    };

    const showError = (msg) => {
      eta.hidden = true;
      if (error) {
        error.textContent = msg;
        error.hidden = false;
      }
      if (hint) hint.hidden = false;
    };

    const render = (pin, place) => {
      const today = todayIST();
      const prefix3 = pin.slice(0, 3);
      const prefix2 = Number(pin.slice(0, 2));
      const district = (place?.district || '').toLowerCase();
      const state = (place?.state || '').toLowerCase();

      const isAhmedabad = prefix3 === '380' || district === 'ahmedabad';
      const isGujarat = isAhmedabad || state === 'gujarat' || (!place && prefix2 >= 36 && prefix2 <= 39);
      const where = place?.district ? `${place.district}${place.state ? ', ' + place.state : ''}` : '';

      if (isAhmedabad) {
        main.innerHTML = `<strong>${ahdText}</strong>`;
        sub.textContent = `Ahmedabad (${pin}) · Free delivery · COD available`;
      } else if (isGujarat) {
        const date = gujDays === 0 ? today : addWorkingDays(today, gujDays);
        main.innerHTML = `Delivery by <strong>${fmt(date)}</strong>`;
        sub.textContent = `${where || 'Gujarat'} (${pin}) · Free delivery · COD available`;
      } else {
        const from = addWorkingDays(today, minDays);
        const to = addWorkingDays(today, maxDays);
        main.innerHTML = `Delivery between <strong>${fmt(from)} – ${fmt(to)}</strong>`;
        sub.textContent = `${where ? where + ' ' : ''}(${pin}) · Free delivery · COD available`;
      }
      if (error) error.hidden = true;
      if (hint) hint.hidden = true;
      eta.hidden = false;
      setMode('change');
    };

    const check = async (pin, { silent = false } = {}) => {
      if (!/^[1-9]\d{5}$/.test(pin)) {
        if (!silent) showError('Please enter a valid 6-digit pincode.');
        return;
      }
      btn.disabled = true;
      const place = await lookup(pin);
      btn.disabled = false;
      if (place?.invalid) {
        if (!silent) showError('We could not find this pincode. Please check and try again.');
        return;
      }
      storage.set(pin);
      render(pin, place);
    };

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      if (btn.dataset.mode === 'change') {
        setMode('check');
        eta.hidden = true;
        if (hint) hint.hidden = false;
        input.value = '';
        input.focus();
        return;
      }
      check(input.value.trim());
    });

    input.addEventListener('input', () => {
      input.value = input.value.replace(/\D/g, '').slice(0, 6);
      if (error) error.hidden = true;
    });

    const saved = storage.get();
    if (saved) {
      input.value = saved;
      check(saved, { silent: true });
    }
  }

  const boot = () => document.querySelectorAll('[data-lpt-delivery]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
